import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Phone, User, Calendar, AtSign, FileText, Instagram, Facebook, Edit } from "lucide-react";
import { Logo } from "@/components/ui/logo";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router";
import { format } from "date-fns";
import { InterestsList } from "@/components/interests/InterestsList";
import { useToast } from "@/hooks/use-toast";
import { CookieSettingsButton } from "@/components/CookieBanner";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState } from "react";
import { BackgroundImage } from "@/components/layout/BackgroundImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProfileForm } from "@/components/settings/ProfileForm";
import { Loader, Mail, Gift, Heart, LogIn, UserPlus, Info, Sparkles, Share2, Shield, HelpCircle, Rocket, Star, Bell, Users, Plus, Lock, Activity, Chrome, MessageCircleQuestion, Skull, Brain } from "lucide-react";
import { AvatarSettings } from "@/components/settings/AvatarSettings";
import { lazy, Suspense } from "react";
const INCLUDE_FAQ_CHAT = import.meta.env.VITE_CAPACITOR_BUILD !== "1";
const LandingFaqChat = INCLUDE_FAQ_CHAT
  ? lazy(() => import("@/components/landing/LandingFaqChat"))
  : null;
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarsApi } from "@/api/user/avatars";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { wishlistApi } from "@/api/wishlist";
import { userApi } from "@/api/user";
import { useEvents } from "@/hooks/useEvents";
import { useReminders } from "@/hooks/useReminders";
import { EventStatus } from "@/api/eventManagement";
import { isNative } from "@/utils/platform";

const Index = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editField, setEditField] = useState<string | null>(null);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null);

  // Fetch real data for stats
  const { data: wishlistItems = [] } = useQuery({
    queryKey: ['wishlist-items', user?.id],
    queryFn: () => user?.id ? wishlistApi.getWishlist(user.id) : [],
    enabled: !!user?.id,
  });

  const { data: friends = [] } = useQuery({
    queryKey: ['friends', user?.id],
    queryFn: () => user?.id ? userApi.getFriends(user.id) : [],
    enabled: !!user?.id,
  });

  const { events = [] } = useEvents(user?.id || 0, 1, 100);

  const { reminders = [] } = useReminders();

  // Calculate stats
  const wishlistCount = wishlistItems.filter((item: any) => !item.is_archived).length;
  const friendsCount = friends.length;
  const upcomingEventsCount = events.filter((e: any) => e.status === EventStatus.PUBLISHED).length;
  const activeRemindersCount = reminders.filter((r: any) => r.is_active).length;

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleAvatarDialogClose = () => {
    setAvatarDialogOpen(false);
    if (user?.id) {
      queryClient.invalidateQueries({
        queryKey: ['user', user.id]
      });
    }
  };

  useEffect(() => {
    const fetchCurrentAvatar = async () => {
      if (user?.profile_picture) {
        try {
          const avatarData = await avatarsApi.getAvatarById(user.profile_picture);
          setCurrentAvatarUrl(avatarData.url);
        } catch (error) {
          // Handle error silently
        }
      } else {
        setCurrentAvatarUrl(null);
      }
    };

    fetchCurrentAvatar();
  }, [user?.profile_picture]);

  const handleCopyNickname = async () => {
    if (user?.nickname) {
      try {
        await navigator.clipboard.writeText(user.nickname);
        toast({
          title: translate("toast.copied.title"),
          description: translate("toast.copied.description"),
        });
      } catch (err) {
        toast({
          title: translate("toast.copyfailed.title"),
          description: translate("toast.copyfailed.description"),
          variant: "destructive",
        });
      }
    }
  };

  const handleEdit = (field: string) => {
    setEditField(field);
    setEditDialogOpen(true);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F1F0FB] to-[#E5DEFF]">
        {/* Top Navigation Bar */}
        <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border shadow-sm pt-[env(safe-area-inset-top)]">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <Logo size="sm" />
              <div className="flex items-center gap-1.5 sm:gap-3">
                <LanguageToggle />
                <ThemeToggle />
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground px-2 sm:px-4 text-xs sm:text-sm"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  <span>{translate("button.signin")}</span>
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  size="sm"
                  className="bg-gradient-to-r from-primary to-primary/80 hover:opacity-90 px-3 sm:px-4 text-xs sm:text-sm"
                >
                  <Rocket className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{translate("button.signup")}</span>
                  <span className="sm:hidden">{translate("button.signup")}</span>
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <BackgroundImage />
          <div className="max-w-7xl mx-auto pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-24 px-3 sm:px-4 lg:px-8 relative">
            <div className="text-center space-y-4 sm:space-y-6 animate-fade-in">
              <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-primary/10 rounded-full mb-2 sm:mb-4">
                <span className="text-xs sm:text-sm font-medium text-primary flex items-center gap-1.5 sm:gap-2">
                  <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline">{translate("home.hero.badge.full")}</span>
                  <span className="xs:hidden">{translate("home.hero.badge.short")}</span>
                </span>
              </div>
              <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent leading-tight px-2">
                {translate("home.hero.title")}
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed px-4">
                {translate("home.hero.subtitle")}
              </p>

              {/* Primary CTA */}
              <div className="flex flex-col items-center gap-3 sm:gap-4 pt-2 sm:pt-4 px-4">
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  className="group w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-7 text-base sm:text-lg font-semibold bg-gradient-to-r from-primary to-primary/80 hover:shadow-xl active:scale-95 sm:hover:scale-105 transition-all shadow-lg"
                >
                  <Rocket className="mr-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:animate-bounce" />
                  {translate("button.signup")}
                  <span className="ml-2">→</span>
                </Button>
                <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <Button
                    onClick={() => navigate('/create-wishlist')}
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-7 text-base sm:text-lg font-semibold border-2 hover:bg-accent/50 active:scale-95 sm:hover:scale-105 transition-all"
                  >
                    <Gift className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    {translate("home.hero.button.instant")}
                  </Button>
                  <Button
                    onClick={() => navigate('/public-randomizer')}
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-7 text-base sm:text-lg font-semibold border-2 hover:bg-accent/50 active:scale-95 sm:hover:scale-105 transition-all"
                  >
                    <Users className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    {translate("home.hero.button.public.Randomizer")}
                  </Button>
                </div>

                {/* App store badges (web only) */}
                {!isNative() && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 pt-2 sm:pt-3">
                  <a
                    href="https://apps.apple.com/de/app/buyfors/id6763368357"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={translate("home.hero.appstore.cta")}
                    className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-black text-white border border-white/10 hover:opacity-90 active:scale-95 sm:hover:scale-105 transition-all shadow-md"
                  >
                    <svg
                      role="img"
                      aria-hidden="true"
                      viewBox="0 0 384 512"
                      className="h-6 w-6 sm:h-7 sm:w-7 fill-current"
                    >
                      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.6 80c30-35.6 27.3-67.9 26.4-79.6-26.5 1.5-57.2 18-74.7 38.3-19.3 21.8-30.6 48.7-28.2 79 28.6 2.2 54.7-12.5 76.5-37.7z" />
                    </svg>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] sm:text-xs opacity-80">
                        {translate("home.hero.appstore.badge.prefix")}
                      </span>
                      <span className="text-sm sm:text-base font-semibold">
                        App Store
                      </span>
                    </span>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.buyfors.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={translate("home.hero.googleplay.cta")}
                    className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-black text-white border border-white/10 hover:opacity-90 active:scale-95 sm:hover:scale-105 transition-all shadow-md"
                  >
                    <svg
                      role="img"
                      aria-hidden="true"
                      viewBox="0 0 512 512"
                      className="h-6 w-6 sm:h-7 sm:w-7"
                    >
                      <path fill="#00d3ff" d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256z" />
                      <path fill="#00f076" d="M325.3 234.3L104.6 13l280.8 161.2z" />
                      <path fill="#ffcf47" d="M413.3 191.5l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8z" />
                      <path fill="#ff414b" d="M104.6 499l280.8-161.2-60.1-60.1z" />
                    </svg>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] sm:text-xs opacity-80">
                        {translate("home.hero.googleplay.badge.prefix")}
                      </span>
                      <span className="text-sm sm:text-base font-semibold">
                        Google Play
                      </span>
                    </span>
                  </a>
                </div>
                )}
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-3 sm:gap-6 pt-6 sm:pt-8 text-xs sm:text-sm text-muted-foreground px-4">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                  <span>{translate("home.trust.secure")}</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                  <span>{translate("home.trust.free")}</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Users className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                  <span>{translate("home.trust.social")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-accent/30 backdrop-blur-sm py-10 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="text-center mb-8 sm:mb-12 md:mb-16 px-4">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-2 sm:mb-4">
                {translate("home.section.why")}
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                {translate("home.features.subtitle")}
              </p>
            </div>

            {/* Top 3 Features - Larger Cards Row 1 */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <Card
                className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/smart-wishlists')}
              >
                <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Gift className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                  {translate("home.feature.wishlist.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {translate("home.feature.wishlist.description")}
                </p>
                <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {translate("home.features.learnmore")}
                </div>
              </Card>

              <Card
                className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/ai-gift-assistant')}
              >
                <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                  {translate("home.feature.ai.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {translate("home.feature.ai.description")}
                </p>
                <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {translate("home.features.learnmore")}
                </div>
              </Card>

              <Card
                className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/social-gifting')}
              >
                <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Share2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                  {translate("home.feature.social.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {translate("home.feature.social.description")}
                </p>
                <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {translate("home.features.learnmore")}
                </div>
              </Card>
            </div>

            {/* Row 2 - 3 Features */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <Card
                className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/give-whats-wanted')}
              >
                <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Heart className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                  {translate("home.feature.wanted.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {translate("home.feature.wanted.description")}
                </p>
                <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {translate("home.features.learnmore")}
                </div>
              </Card>

              <Card
                className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/randomizer-info')}
              >
                <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Users className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                  {translate("home.feature.Randomizer.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {translate("home.feature.Randomizer.description")}
                </p>
                <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {translate("home.features.learnmore")}
                </div>
              </Card>

              <Card
                className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/event-management')}
              >
                <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Calendar className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                  {translate("home.feature.eventmanagement.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {translate("home.feature.eventmanagement.description")}
                </p>
                <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {translate("home.features.learnmore")}
                </div>
              </Card>
            </div>

            {/* Row 3 - 3 Features */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <Card
                className="group h-full flex flex-col p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/price-drop-alerts')}
              >
                <div className="flex-1 flex flex-col">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Bell className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                    {translate("home.feature.price.title")}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {translate("home.feature.price.description")}
                  </p>
                  <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                    {translate("home.features.learnmore")}
                  </div>
                </div>
              </Card>

              <Card
                className="group h-full flex flex-col p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/chrome-extension')}
              >
                <div className="flex-1 flex flex-col">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Chrome className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                    {translate("home.feature.chrome.title")}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {translate("home.feature.chrome.description")}
                  </p>
                  <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                    {translate("home.features.learnmore")}
                  </div>
                </div>
              </Card>

              <Card
                className="group h-full flex flex-col p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-dashed hover:border-primary/30 cursor-pointer"
                onClick={() => navigate('/daily-quiz')}
              >
                <div className="flex-1 flex flex-col">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                    {translate("home.feature.dailyquiz.title")}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {translate("home.feature.dailyquiz.description")}
                  </p>
                  <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                    {translate("home.feature.dailyquiz.cta")}
                  </div>
                </div>
              </Card>
            </div>

            {/* Row 4 - 3 Features */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <Card
                className="group h-full flex flex-col p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/friend-quiz-info')}
              >
                <div className="flex-1 flex flex-col">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <MessageCircleQuestion className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                    {translate("home.feature.friendquiz.title")}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {translate("home.feature.friendquiz.description")}
                  </p>
                  <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                    {translate("home.features.learnmore")}
                  </div>
                </div>
              </Card>

              <Card
                className="group h-full flex flex-col p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/mafia-info')}
              >
                <div className="flex-1 flex flex-col">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Skull className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                    {translate("home.feature.mafia.title")}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {translate("home.feature.mafia.description")}
                  </p>
                  <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                    {translate("home.features.learnmore")}
                  </div>
                </div>
              </Card>

              <Card
                className="group h-full flex flex-col p-5 sm:p-6 md:p-8 hover:shadow-2xl active:scale-95 sm:hover:-translate-y-2 transition-all duration-300 bg-card border-2 border-transparent hover:border-primary/20 cursor-pointer"
                onClick={() => navigate('/sync-info')}
              >
                <div className="flex-1 flex flex-col">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Brain className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 text-foreground group-hover:text-primary transition-colors">
                    {translate("home.feature.sync.title")}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {translate("home.feature.sync.description")}
                  </p>
                  <div className="mt-3 sm:mt-4 text-sm sm:text-base text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                    {translate("home.features.learnmore")}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* FAQ Chat Widget — web-only, skipped on native to keep app bundle small */}
        {LandingFaqChat && !isNative() && (
          <Suspense fallback={null}>
            <LandingFaqChat />
          </Suspense>
        )}

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto py-10 sm:py-16 md:py-20 px-3 sm:px-4 lg:px-8">
          <Card className="p-6 sm:p-10 md:p-12 lg:p-16 bg-gradient-to-br from-primary via-primary/90 to-primary/80 border-none shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,transparent,black)]" />
            <div className="relative z-10 text-center space-y-4 sm:space-y-6">
              <div className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full mb-2 sm:mb-4">
                <span className="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5 sm:gap-2">
                  <Star className="h-3 w-3 sm:h-4 sm:w-4" />
                  {translate("home.cta.badge")}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight px-4">
                {translate("home.cta.title")}
              </h2>
              <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed px-4">
                {translate("home.cta.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-2 sm:pt-4 px-4">
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  className="group w-full sm:w-auto px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg font-semibold bg-white dark:bg-gray-800 text-primary hover:bg-white/90 dark:hover:bg-gray-700 active:scale-95 sm:hover:shadow-2xl sm:hover:scale-105 transition-all"
                >
                  <Rocket className="mr-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:animate-bounce" />
                  {translate("button.signup")}
                  <span className="ml-2">→</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Footer */}
        <footer className="bg-accent/50 backdrop-blur-sm border-t border-border">
          <div className="max-w-7xl mx-auto py-8 sm:py-10 md:py-12 px-3 sm:px-4 lg:px-8">
            <nav className="flex flex-wrap justify-center gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-3 sm:gap-y-4 mb-6 sm:mb-8">
              <Link
                to="/about"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <Info className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("nav.about")}</span>
              </Link>
              <a
                href="https://status.buyfors.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("footer.link.status")}</span>
              </a>
              <Link
                to="/contact"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <Mail className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("nav.contact")}</span>
              </Link>
              <Link
                to="/faq"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <HelpCircle className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("nav.faq")}</span>
              </Link>
              <Link
                to="/privacy-policy"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <Shield className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("nav.privacy")}</span>
              </Link>
              <Link
                to="/terms-of-service"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("nav.terms")}</span>
              </Link>
              <Link
                to="/impressum"
                className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground hover:text-primary transition-colors text-xs sm:text-sm touch-manipulation"
              >
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{translate("nav.impressum")}</span>
              </Link>
            </nav>
            <div className="text-center text-xs sm:text-sm text-muted-foreground px-4">
              {translate("footer.copyright").replace("{year}", new Date().getFullYear().toString())}
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Welcome Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 p-8 shadow-xl">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,transparent,black)]" />
          <div className="relative z-10 flex items-center gap-6">
            <Avatar className="h-20 w-20 border-4 border-white/20 shadow-lg">
              {currentAvatarUrl ? (
                <AvatarImage src={currentAvatarUrl} alt="Profile" className="object-cover" />
              ) : (
                <AvatarFallback className="bg-white/20 text-white text-2xl">
                  <User className="h-10 w-10" />
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-1">
                Welcome back, {user.full_name?.split(' ')[0] || user.nickname}!
              </h1>
              <p className="text-white/80">
                Here's what's happening with your wishlists and events
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card
            className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50 cursor-pointer"
            onClick={() => navigate('/wishlist')}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <p className="text-sm font-medium text-muted-foreground">Your Wishlist</p>
                <h3 className="text-3xl font-bold text-foreground">{wishlistCount}</h3>
                <p className="text-sm text-muted-foreground">items</p>
              </div>
              <div className="rounded-full p-3 bg-gradient-to-br from-primary/20 to-primary/10">
                <Gift className="h-6 w-6 text-primary" />
              </div>
            </div>
          </Card>

          <Card
            className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50 cursor-pointer"
            onClick={() => navigate('/friends')}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <p className="text-sm font-medium text-muted-foreground">Friends</p>
                <h3 className="text-3xl font-bold text-foreground">{friendsCount}</h3>
                <p className="text-sm text-muted-foreground">connections</p>
              </div>
              <div className="rounded-full p-3 bg-gradient-to-br from-blue-500/20 to-blue-500/10">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </Card>

          <Card
            className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50 cursor-pointer"
            onClick={() => navigate('/events')}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <p className="text-sm font-medium text-muted-foreground">Events</p>
                <h3 className="text-3xl font-bold text-foreground">{upcomingEventsCount}</h3>
                <p className="text-sm text-muted-foreground">upcoming</p>
              </div>
              <div className="rounded-full p-3 bg-gradient-to-br from-green-500/20 to-green-500/10">
                <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </Card>

          <Card
            className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50 cursor-pointer"
            onClick={() => navigate('/reminders')}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <p className="text-sm font-medium text-muted-foreground">Reminders</p>
                <h3 className="text-3xl font-bold text-foreground">{activeRemindersCount}</h3>
                <p className="text-sm text-muted-foreground">active</p>
              </div>
              <div className="rounded-full p-3 bg-gradient-to-br from-orange-500/20 to-orange-500/10">
                <Bell className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="ml-1">
          <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card
              className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group border-border/50"
              onClick={() => navigate('/wishlist')}
            >
              <div className="space-y-4">
                <div className="rounded-full w-12 h-12 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/10 group-hover:scale-110 transition-transform">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                    Add Wishlist Item
                  </h3>
                  <p className="text-sm text-muted-foreground">Create a new item for your wishlist</p>
                </div>
              </div>
            </Card>

            <Card
              className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group border-border/50"
              onClick={() => navigate('/events')}
            >
              <div className="space-y-4">
                <div className="rounded-full w-12 h-12 flex items-center justify-center bg-gradient-to-br from-green-500/20 to-green-500/10 group-hover:scale-110 transition-transform">
                  <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-green-600 dark:text-green-400 transition-colors">
                    Create Event
                  </h3>
                  <p className="text-sm text-muted-foreground">Plan a new celebration or gathering</p>
                </div>
              </div>
            </Card>

            <Card
              className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group border-border/50"
              onClick={() => navigate('/friends?tab=search')}
            >
              <div className="space-y-4">
                <div className="rounded-full w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500/20 to-blue-500/10 group-hover:scale-110 transition-transform">
                  <UserPlus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-blue-600 dark:text-blue-400 transition-colors">
                    Add Friends
                  </h3>
                  <p className="text-sm text-muted-foreground">Connect with friends and family</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Your Profile - Reorganized */}
        <div className="space-y-6 ml-1">
          <h2 className="text-2xl font-bold">Your Profile</h2>

          {/* Personal Information & Contact Cards - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Information Card */}
            <Card className="p-6 shadow-lg border-border/50 hover:shadow-xl transition-all">
              <h3 className="text-lg font-semibold mb-4 text-primary">Personal Information</h3>
              <div className="space-y-4">
                {/* Avatar - Large and Prominent */}
                <div className="flex justify-center mb-6">
                  <button
                    type="button"
                    onClick={() => setAvatarDialogOpen(true)}
                    aria-label="Change avatar"
                    className="relative rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/30"
                  >
                    <Avatar className="h-24 w-24 hover:ring-4 hover:ring-primary/30 transition-all shadow-xl border-4 border-primary/20 overflow-hidden bg-gradient-to-br from-[#F1F0FB] to-[#E5DEFF]">
                      {currentAvatarUrl ? (
                        <AvatarImage src={currentAvatarUrl} alt="Profile picture" className="object-cover" />
                      ) : (
                        <AvatarFallback className="bg-gradient-to-br from-[#9b87f5]/10 to-[#D946EF]/10">
                          <User className="h-12 w-12 text-[#7E69AB]" />
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <span className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-lg border-2 border-background">
                      <Edit className="h-4 w-4" />
                    </span>
                  </button>
                </div>

                {/* Full Name */}
                <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('name')}>
                  <div className="p-2 rounded-full bg-primary/20">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-medium">Full Name</p>
                    <p className="text-foreground font-semibold">{user.full_name}</p>
                  </div>
                  <Edit className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Nickname */}
                <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-purple-500/5 to-purple-500/10 border border-purple-500/20 dark:border-purple-500/30 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('nickname')}>
                  <div className="p-2 rounded-full bg-purple-500/20">
                    <AtSign className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-medium">Nickname</p>
                    <p className="text-foreground font-semibold">{user.nickname}</p>
                  </div>
                  <Edit className="h-4 w-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Email */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-blue-500/5 to-blue-500/10 border border-blue-500/20 opacity-75">
                  <div className="p-2 rounded-full bg-blue-500/20">
                    <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      Email
                    </p>
                    <p className="text-foreground font-semibold text-sm">{user.email}</p>
                  </div>
                  <Lock className="h-4 w-4 text-blue-600 dark:text-blue-400/50" />
                </div>
              </div>
            </Card>

            {/* Contact & Important Dates Card */}
            <Card className="p-6 shadow-lg border-border/50 hover:shadow-xl transition-all">
              <h3 className="text-lg font-semibold mb-4 text-primary">Contact & Dates</h3>
              <div className="space-y-4">
                {/* Birthday */}
                <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-pink-500/5 to-pink-500/10 border border-pink-500/20 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('birthday')}>
                  <div className="p-2 rounded-full bg-pink-500/20">
                    <Calendar className="h-5 w-5 text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-medium">Birthday</p>
                    <p className={`font-semibold ${!user.birthday ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                      {user.birthday ? format(new Date(user.birthday), 'MMMM dd, yyyy') : 'Not set'}
                    </p>
                  </div>
                  <Edit className="h-4 w-4 text-pink-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Gender */}
                <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-purple-500/5 to-purple-500/10 border border-purple-500/20 dark:border-purple-500/30 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('gender')}>
                  <div className="p-2 rounded-full bg-purple-500/20">
                    <User className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-medium">Gender</p>
                    <p className={`font-semibold ${!user.gender ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                      {user.gender ? (user.gender === 'male' ? 'Male' : user.gender === 'female' ? 'Female' : user.gender === 'non-binary' ? 'Non-binary' : user.gender === 'prefer_not_to_say' ? 'Prefer not to say' : user.gender) : 'Not set'}
                    </p>
                  </div>
                  <Edit className="h-4 w-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Phone */}
                <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('phoneNumber')}>
                  <div className="p-2 rounded-full bg-green-500/20">
                    <Phone className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-medium">Phone Number</p>
                    <p className={`font-semibold ${!user.phone_number ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                      {user.phone_number || 'Not set'}
                    </p>
                  </div>
                  <Edit className="h-4 w-4 text-green-600 dark:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </Card>
          </div>

          {/* About You Card - Full Width */}
          <Card className="p-6 shadow-lg border-border/50 hover:shadow-xl transition-all">
            <h3 className="text-lg font-semibold mb-4 text-primary">About You</h3>
            <div className="group flex items-start gap-3 p-4 rounded-lg bg-gradient-to-br from-amber-500/5 to-amber-500/10 border border-amber-500/20 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('bio')}>
              <div className="p-2 rounded-full bg-amber-500/20 mt-1">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-medium mb-2">Bio</p>
                <p className={`${!user.bio ? 'text-muted-foreground italic' : 'text-foreground'} leading-relaxed`}>
                  {user.bio || 'Tell others about yourself...'}
                </p>
              </div>
              <Edit className="h-4 w-4 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
            </div>
          </Card>

          {/* Interests Section - Full Width */}
          <InterestsList userId={user.id} />

          {/* Social Connections Card - Full Width */}
          <Card className="p-6 shadow-lg border-border/50 hover:shadow-xl transition-all">
            <h3 className="text-lg font-semibold mb-4 text-primary">Social Connections</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Instagram */}
              <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-pink-500/5 to-pink-500/10 border border-pink-500/20 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('instagram')}>
                <div className="p-2 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                  <Instagram className="h-5 w-5 text-[#E1306C]" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground font-medium">Instagram</p>
                  <p className={`font-semibold ${!user.instagram ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                    {user.instagram || 'Not connected'}
                  </p>
                </div>
                <Edit className="h-4 w-4 text-[#E1306C] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              {/* Facebook */}
              <div className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-blue-600/5 to-blue-600/10 border border-blue-600/20 cursor-pointer hover:shadow-md transition-all" onClick={() => handleEdit('facebook')}>
                <div className="p-2 rounded-full bg-blue-600/20">
                  <Facebook className="h-5 w-5 text-[#1877F2]" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground font-medium">Facebook</p>
                  <p className={`font-semibold ${!user.facebook ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                    {user.facebook || 'Not connected'}
                  </p>
                </div>
                <Edit className="h-4 w-4 text-[#1877F2] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </Card>
        </div>

        {/* Dialogs */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Edit Profile</DialogTitle>
            </DialogHeader>
            {user && (
              <ProfileForm
                defaultValues={{
                  ...user,
                  name: user.full_name || "",
                  email: user.email || "",
                  birthday: user.birthday || undefined,
                  phoneNumber: user.phone_number || "",
                  nickname: user.nickname || "",
                  gender: user.gender || "",
                  bio: user.bio || "",
                  instagram: user.instagram || "",
                  facebook: user.facebook || "",
                  otherGender: "",
                }}
                fieldToEdit={editField}
                onClose={handleEditDialogClose}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Avatar</DialogTitle>
            </DialogHeader>
            <AvatarSettings onClose={handleAvatarDialogClose} />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Index;
